<template>
  <div class="pb-10">
    <PageTitle title="Gestion des clients" :description="`${pagination.total || 0} client(s) au total`">
      <template #actions>
        <BaseButton variant="outline-primary" @click="downloadUnpaidList" :loading="isDownloading">
          <template #icon-left>
            <i class="fa-solid fa-file-pdf"></i>
          </template>
          Télécharger liste impayés
        </BaseButton>
        <BaseButton variant="primary" @click="onAddCustomer">
          <template #icon-left>
            <i class="fa-solid fa-plus"></i>
          </template>
          Nouveau client
        </BaseButton>
      </template>
    </PageTitle>

    <!-- Filtres -->
    <BaseCard class="mb-6">
      <div class="flex flex-wrap gap-4 items-center">
        <!-- Recherche -->
        <div class="flex-1 min-w-[300px]">
          <BaseInput v-model="searchQuery" type="search" placeholder="Rechercher un client..." clearable
            @input="onSearchInput">
            <template #icon-left>
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </template>
          </BaseInput>
        </div>

        <!-- Filtre impayés -->
        <BaseButton :variant="showUnpaidOnly ? 'primary' : 'outline-primary'" @click="toggleUnpaidFilter">
          <template #icon-left>
            <i class="fa-solid fa-exclamation-triangle"></i>
          </template>
          {{ showUnpaidOnly ? 'Tous les clients' : 'Clients avec impayés' }}
        </BaseButton>
      </div>
    </BaseCard>

    <!-- Filtres actifs -->
    <BaseCard v-if="hasActiveFilters" class="mb-6" variant="bordered">
      <div class="flex flex-wrap items-center gap-3">
        <span class="text-sm font-medium text-gray-600">Filtres actifs:</span>
        <span v-if="searchQuery"
          class="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
          Recherche: "{{ searchQuery }}"
          <button @click="clearSearch" class="hover:bg-blue-200 rounded-full p-0.5 transition-colors">
            <i class="fa-solid fa-times text-xs"></i>
          </button>
        </span>
        <span v-if="showUnpaidOnly"
          class="inline-flex items-center gap-2 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
          Clients avec impayés
          <button @click="toggleUnpaidFilter" class="hover:bg-orange-200 rounded-full p-0.5 transition-colors">
            <i class="fa-solid fa-times text-xs"></i>
          </button>
        </span>
        <BaseButton variant="ghost" size="sm" @click="clearAllFilters" class="ml-auto">
          Effacer tous les filtres
        </BaseButton>
      </div>
    </BaseCard>

    <!-- Loader -->
    <BaseCard v-if="loading">
      <div class="flex flex-col items-center justify-center py-20">
        <div class="w-10 h-10 border-4 border-gray-200 border-t-primary rounded-full animate-spin mb-4"></div>
        <p class="text-gray-600">Chargement des clients...</p>
      </div>
    </BaseCard>

    <!-- Message quand aucun client n'est trouvé -->
    <BaseCard v-else-if="!loading && customers.length === 0">
      <div class="flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 text-gray-300 mb-4">
          <i class="fa-solid fa-users text-6xl"></i>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">Aucun client trouvé</h3>
        <p class="text-gray-600 max-w-md mb-6">
          <span v-if="hasActiveFilters">Aucun client ne correspond à vos critères de recherche.</span>
          <span v-else>Commencez par ajouter votre premier client.</span>
        </p>
        <BaseButton v-if="hasActiveFilters" variant="primary" @click="clearAllFilters">
          Réinitialiser les filtres
        </BaseButton>
        <BaseButton v-else variant="primary" @click="onAddCustomer">
          <template #icon-left>
            <i class="fa-solid fa-plus"></i>
          </template>
          Ajouter un client
        </BaseButton>
      </div>
    </BaseCard>

    <!-- Table des clients -->
    <BaseCard v-else>
      <div class="overflow-x-auto">
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-gray-50 border-b-2 border-gray-200">
              <th
                class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[200px]">
                Nom
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[180px]">
                Contact
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[120px]">
                Ventes
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[120px]">
                Impayés
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[100px]">
                Statut
              </th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[120px]">
                Créé le
              </th>
              <th class="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-[120px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="customer in customers" :key="customer.id" class="hover:bg-gray-50 transition-colors">
              <td class="px-4 py-4">
                <div class="flex items-center gap-3 cursor-pointer group" @click="viewCustomer(customer)">
                  <div
                    class="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
                    <i class="fa-solid fa-user text-primary text-lg"></i>
                  </div>
                  <div class="flex flex-col min-w-0">
                    <span
                      class="font-semibold text-gray-900 text-sm group-hover:text-primary transition-colors uppercase">{{
                        customer.name }}</span>
                    <span v-if="customer.company" class="text-xs text-gray-600 truncate">{{ customer.company }}</span>
                  </div>
                </div>
              </td>
              <td class="px-4 py-4">
                <div class="flex flex-col gap-1">
                  <div v-if="customer.phone" class="flex items-center gap-2 text-sm text-gray-700">
                    <i class="fa-solid fa-phone text-gray-400 text-xs"></i>
                    <span>{{ customer.phone }}</span>
                  </div>
                  <div v-if="customer.email" class="flex items-center gap-2 text-sm text-gray-700">
                    <i class="fa-solid fa-envelope text-gray-400 text-xs"></i>
                    <span class="truncate">{{ customer.email }}</span>
                  </div>
                  <span v-if="!customer.phone && !customer.email" class="text-xs text-gray-400 italic">
                    Aucun contact
                  </span>
                </div>
              </td>
              <td class="px-4 py-4">
                <span class="text-sm font-semibold text-primary">
                  {{ formatCurrency(customer.total_spent || 0) }}
                </span>
              </td>
              <td class="px-4 py-4">
                <div class="flex flex-col gap-1">
                  <div class="flex items-baseline gap-1">
                    <span :class="[
                      'text-sm font-semibold',
                      customer.total_unpaid > 0 ? 'text-orange-600' : 'text-gray-400'
                    ]">
                      {{ customer.unpaid_sales_count || 0 }}
                    </span>
                    <span class="text-xs text-gray-500">factures</span>
                  </div>
                  <div class="flex items-baseline gap-1">
                    <span :class="[
                      'text-sm font-semibold',
                      customer.total_unpaid > 0 ? 'text-orange-600' : 'text-gray-400'
                    ]">
                      {{ formatCurrency(customer.total_unpaid || 0) }}
                    </span>
                    <span class="text-xs text-gray-500">à payer</span>
                  </div>
                </div>
              </td>
              <td class="px-4 py-4">
                <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                  :class="customer.status ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'">
                  <span class="w-1.5 h-1.5 rounded-full mr-1.5"
                    :class="customer.status ? 'bg-green-500' : 'bg-gray-400'"></span>
                  {{ customer.status ? 'Actif' : 'Inactif' }}
                </span>
              </td>
              <td class="px-4 py-4">
                <span class="text-sm text-gray-600">{{ formatDate(customer.created_at) }}</span>
              </td>
              <td class="px-4 py-4">
                <div class="flex items-center justify-center gap-2">
                  <BaseButton variant="icon" size="sm" @click="editCustomer(customer)" title="Modifier">
                    <i class="fa-solid fa-edit text-primary"></i>
                  </BaseButton>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && customers.length > 0" class="border-t border-gray-200">
        <PaginationComponent :current-page="pagination.currentPage" :per-page="pagination.perPage"
          :total="pagination.total" :from="pagination.from" :to="pagination.to" :show-page-numbers="true"
          :max-visible-pages="5" @page-change="goToPage" />
      </div>
    </BaseCard>

    <!-- Modal de création -->
    <CreateCustomerModal :show="showCreateModal" @close="closeCreateModal" @created="onCustomerCreated" />

    <!-- Modal d'édition -->
    <CustomerEditModal :show="showEditModal" :customer="selectedCustomer" @close="closeEditModal"
      @updated="onCustomerUpdated" />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import customerService from '@/services/customerService'
import PageTitle from '@/components/common/PageTitle.vue'
import BaseCard from '@/components/base/BaseCard.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import CustomerEditModal from '@/components/customers/CustomerEditModal.vue'
import CreateCustomerModal from '@/components/customers/CreateCustomerModal.vue'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'CustomerList',
  components: {
    PageTitle,
    BaseCard,
    BaseButton,
    BaseInput,
    PaginationComponent,
    CustomerEditModal,
    CreateCustomerModal
  },
  setup() {
    const router = useRouter()
    const toastStore = useToastStore()

    // État
    const customers = ref([])
    const loading = ref(true)
    const isDownloading = ref(false)
    const searchQuery = ref('')
    const searchTimeout = ref(null)
    const showUnpaidOnly = ref(false)

    // Modal d'édition
    const showEditModal = ref(false)
    const selectedCustomer = ref(null)

    // Modal de création
    const showCreateModal = ref(false)

    // Pagination
    const pagination = ref({
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      from: 0,
      to: 0
    })

    // Propriétés calculées
    const hasActiveFilters = computed(() => {
      return searchQuery.value || showUnpaidOnly.value
    })

    // Méthodes de chargement
    const loadCustomers = async (params = {}) => {
      try {
        loading.value = true
        const response = await customerService.getCustomers({
          page: pagination.value.currentPage,
          per_page: pagination.value.perPage,
          search: searchQuery.value,
          with_unpaid: showUnpaidOnly.value,
          ...params
        })

        const data = response.data
        customers.value = data.data || []

        // Mettre à jour la pagination selon la structure de l'API
        if (data.meta) {
          const meta = data.meta
          const perPage = meta.per_page || pagination.value.perPage
          const total = meta.total || 0
          const calculatedLastPage = perPage > 0 ? Math.ceil(total / perPage) : 1

          pagination.value = {
            currentPage: pagination.value.currentPage, // Garder la page actuelle
            perPage: perPage,
            total: total,
            lastPage: calculatedLastPage > 0 ? calculatedLastPage : 1,
            from: meta.from || 0,
            to: meta.to || 0
          }
        } else {
          // Fallback si pas de meta
          pagination.value.total = customers.value.length
          pagination.value.from = 1
          pagination.value.to = customers.value.length
          pagination.value.lastPage = 1
        }
      } catch (error) {
        console.error('Erreur lors du chargement des clients:', error)
        customers.value = []
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du chargement des clients',
          duration: 4000
        })
      } finally {
        loading.value = false
      }
    }

    // Méthodes de recherche
    const onSearchInput = () => {
      clearTimeout(searchTimeout.value)
      searchTimeout.value = setTimeout(() => {
        pagination.value.currentPage = 1
        loadCustomers()
      }, 500)
    }

    const clearSearch = () => {
      searchQuery.value = ''
      pagination.value.currentPage = 1
      loadCustomers()
    }

    const clearAllFilters = () => {
      searchQuery.value = ''
      showUnpaidOnly.value = false
      pagination.value.currentPage = 1
      loadCustomers()
    }

    // Méthode pour basculer le filtre des impayés
    const toggleUnpaidFilter = () => {
      showUnpaidOnly.value = !showUnpaidOnly.value
      pagination.value.currentPage = 1
      loadCustomers()
    }

    // Méthodes de pagination
    const goToPage = (page) => {
      const lastPage = pagination.value.lastPage || 1
      if (page < 1 || (lastPage > 0 && page > lastPage)) return
      pagination.value.currentPage = page
      loadCustomers()
    }

    // Méthodes d'actions
    const onAddCustomer = () => {
      showCreateModal.value = true
    }

    const viewCustomer = (customer) => {
      router.push({ name: 'customer-detail', params: { id: customer.id } })
    }

    const editCustomer = (customer) => {
      selectedCustomer.value = customer
      showEditModal.value = true
    }

    const closeEditModal = () => {
      showEditModal.value = false
      selectedCustomer.value = null
    }

    const onCustomerUpdated = (updatedCustomer) => {
      // Mettre à jour le client dans la liste
      const index = customers.value.findIndex(c => c.id === updatedCustomer.id)
      if (index !== -1) {
        customers.value[index] = { ...customers.value[index], ...updatedCustomer }
      }
    }

    const onCustomerCreated = () => {
      // Recharger la liste des clients après création
      loadCustomers()
      showCreateModal.value = false
    }

    const closeCreateModal = () => {
      showCreateModal.value = false
    }

    // Méthodes utilitaires
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR')
    }

    const formatAddress = (customer) => {
      return customerService.formatAddress(customer)
    }

    const downloadUnpaidList = async () => {
      if (isDownloading.value) return
      
      isDownloading.value = true
      try {
        await customerService.downloadUnpaidList(false)
      } catch (error) {
        console.error('Erreur lors du téléchargement de la liste des impayés:', error)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du téléchargement. Veuillez réessayer.',
          duration: 4000
        })
      } finally {
        isDownloading.value = false
      }
    }

    // Nettoyer le timeout lors du démontage
    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }
    })

    // Cycle de vie
    onMounted(() => {
      loadCustomers()
    })

    return {
      // État
      customers,
      loading,
      isDownloading,
      searchQuery,
      showUnpaidOnly,
      pagination,
      showEditModal,
      selectedCustomer,
      showCreateModal,

      // Propriétés calculées
      hasActiveFilters,

      // Méthodes
      loadCustomers,
      onSearchInput,
      clearSearch,
      clearAllFilters,
      toggleUnpaidFilter,
      goToPage,
      onAddCustomer,
      viewCustomer,
      editCustomer,
      closeEditModal,
      onCustomerUpdated,
      onCustomerCreated,
      closeCreateModal,
      formatCurrency,
      formatDate,
      formatAddress,
      downloadUnpaidList
    }
  }
})
</script>